import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface SearchResult {
	sectionId: string;
	sectionTitle: string;
	context: string;
	element: Element;
}

@Component({
	selector: 'bp-handbuch',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './handbuch.component.html',
	styleUrls: ['./handbuch.component.css']
})
export class HandbuchComponent implements OnInit, OnDestroy, AfterViewInit {
	activeSection = 'einfuehrung';
	searchQuery = '';
	searchResults: SearchResult[] = [];
	showSearchResults = false;
	currentResultIndex = -1;
	tocExpanded = false;
	tocOpen = false;
	showScrollTopButton = false;
	seaDownloadStatus: 'idle' | 'downloading' | 'done' | 'error' = 'idle';
	seaDownloadProgress = '';
	private highlightedElements: Element[] = [];

	@ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

	private sections = [
		'einfuehrung', 'projekte', 'karte', 'bereiche', 'layer',
		'bohrpunktliste', 'filter', 'punkte', 'protokoll',
		'import', 'export', 'offline', 'statusanzeigen', 'technik'
	];
	private sectionTitles: Record<string, string> = {
		'einfuehrung': '1. Einführung',
		'projekte': '2. Projekte',
		'karte': '3. Kartenansicht',
		'bereiche': '4. Projektbereiche',
		'layer': '5. Layer',
		'bohrpunktliste': '6. Bohrpunktliste',
		'filter': '7. Filterfunktion',
		'punkte': '8. Bohrpunkte',
		'protokoll': '9. Protokollansicht',
		'import': '10. Datenimport',
		'export': '11. Datenexport',
		'offline': '12. Offline-Modus',
		'statusanzeigen': '13. Statusanzeigen',
		'technik': '14. Technik'
	};
	private scrollHandler: (() => void) | null = null;

	constructor(private location: Location) {}

	ngOnInit() {}

	ngAfterViewInit() {
		const container = document.querySelector('.handbuch-content');
		if (container) {
			this.scrollHandler = () => this.onScroll(container);
			container.addEventListener('scroll', this.scrollHandler);
		}
	}

	ngOnDestroy() {
		const container = document.querySelector('.handbuch-content');
		if (container && this.scrollHandler) {
			container.removeEventListener('scroll', this.scrollHandler);
		}
		this.clearHighlights();
	}

	goBack() {
		this.location.back();
	}

	scrollTo(id: string) {
		this.clearSearch();
		this.tocOpen = false; // TOC schließen auf kleinen Bildschirmen
		const element = document.getElementById(id);
		const container = document.querySelector('.handbuch-content');
		if (element && container) {
			const containerRect = container.getBoundingClientRect();
			const elementRect = element.getBoundingClientRect();
			const scrollTop = container.scrollTop + (elementRect.top - containerRect.top);
			container.scrollTo({ top: scrollTop, behavior: 'smooth' });
		}
	}

	toggleToc() {
		this.tocOpen = !this.tocOpen;
	}

	scrollToTop() {
		const container = document.querySelector('.handbuch-content');
		if (container) {
			container.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}

	onSearch() {
		this.clearHighlights();
		this.searchResults = [];

		if (this.searchQuery.length < 2) {
			this.showSearchResults = false;
			return;
		}

		const query = this.searchQuery.toLowerCase();
		const container = document.querySelector('.handbuch-content');
		if (!container) return;

		// Alle Textelemente durchsuchen
		const walker = document.createTreeWalker(
			container,
			NodeFilter.SHOW_TEXT,
			null
		);

		const matches: { node: Text; index: number }[] = [];
		let node: Text | null;

		while ((node = walker.nextNode() as Text | null)) {
			const text = node.textContent?.toLowerCase() || '';
			let index = text.indexOf(query);
			while (index !== -1) {
				matches.push({ node, index });
				index = text.indexOf(query, index + 1);
			}
		}

		// Erste Treffer verarbeiten, Duplikate vermeiden
		const usedElements = new Set<Element>();
		for (const match of matches.slice(0, 100)) {
			const section = this.findParentSection(match.node);
			if (!section) continue;

			const parentElement = match.node.parentElement;
			if (!parentElement) continue;

			// Prüfen ob dieses Element oder ein Eltern-Element bereits verwendet wird
			let isDuplicate = false;
			let checkElement: Element | null = parentElement;
			while (checkElement && checkElement !== section) {
				if (usedElements.has(checkElement)) {
					isDuplicate = true;
					break;
				}
				checkElement = checkElement.parentElement;
			}
			if (isDuplicate) continue;

			// Prüfen ob ein Kind-Element bereits verwendet wird
			for (const usedEl of usedElements) {
				if (parentElement.contains(usedEl)) {
					isDuplicate = true;
					break;
				}
			}
			if (isDuplicate) continue;

			usedElements.add(parentElement);

			// Kontext extrahieren
			const fullText = match.node.textContent || '';
			const start = Math.max(0, match.index - 30);
			const end = Math.min(fullText.length, match.index + this.searchQuery.length + 30);
			let context = fullText.substring(start, end).trim();
			if (start > 0) context = '...' + context;
			if (end < fullText.length) context = context + '...';

			this.searchResults.push({
				sectionId: section.id,
				sectionTitle: this.sectionTitles[section.id] || section.id,
				context,
				element: parentElement
			});

			if (this.searchResults.length >= 20) break;
		}

		this.showSearchResults = this.searchResults.length > 0;
		this.currentResultIndex = -1;
	}

	goToResult(result: SearchResult) {
		this.currentResultIndex = this.searchResults.indexOf(result);
		this.showSearchResults = false;
		this.clearHighlights();

		// Element highlighten
		if (result.element) {
			result.element.classList.add('search-highlight');
			this.highlightedElements.push(result.element);

			// Zum Element scrollen
			const container = document.querySelector('.handbuch-content');
			if (container) {
				const containerRect = container.getBoundingClientRect();
				const elementRect = result.element.getBoundingClientRect();
				const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - 100;
				container.scrollTo({ top: scrollTop, behavior: 'smooth' });
			}

			// Highlight nach 3 Sekunden entfernen
			setTimeout(() => {
				result.element.classList.remove('search-highlight');
			}, 3000);
		}
	}

	clearSearch() {
		this.searchQuery = '';
		this.searchResults = [];
		this.showSearchResults = false;
		this.clearHighlights();
	}

	onSearchKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			this.clearSearch();
		} else if (event.key === 'Enter' && this.searchResults.length > 0) {
			this.goToNextResult();
		}
	}

	goToNextResult() {
		if (this.searchResults.length === 0) return;
		this.currentResultIndex = (this.currentResultIndex + 1) % this.searchResults.length;
		this.goToResult(this.searchResults[this.currentResultIndex]);
	}

	goToPreviousResult() {
		if (this.searchResults.length === 0) return;
		this.currentResultIndex = this.currentResultIndex <= 0
			? this.searchResults.length - 1
			: this.currentResultIndex - 1;
		this.goToResult(this.searchResults[this.currentResultIndex]);
	}

	async downloadSeaDocs(): Promise<void> {
		// Alle SEA-PDF-Links aus dem DOM sammeln
		const container = document.querySelector('.handbuch-content');
		if (!container) return;
		const links = Array.from(container.querySelectorAll<HTMLAnchorElement>('a[href*="assets/SEA/"]'));
		const urls = [...new Set(links.map(a => a.getAttribute('href')!).filter(Boolean))];
		if (urls.length === 0) return;

		this.seaDownloadStatus = 'downloading';
		let cached = 0;
		let downloaded = 0;
		let failed = 0;

		try {
			for (const url of urls) {
				// Prüfen ob bereits im Cache
				const cachedResponse = await caches.match(url);
				if (cachedResponse) {
					cached++;
					this.seaDownloadProgress = `${cached + downloaded}/${urls.length} (${cached} im Cache)`;
					continue;
				}
				// Nicht im Cache → laden (SW cached automatisch)
				try {
					await fetch(url);
					downloaded++;
				} catch {
					failed++;
				}
				this.seaDownloadProgress = `${cached + downloaded}/${urls.length}` + (cached > 0 ? ` (${cached} im Cache)` : '');
			}
			this.seaDownloadStatus = failed > 0 ? 'error' : 'done';
			this.seaDownloadProgress = failed > 0 ? `${failed} fehlgeschlagen` : `${urls.length} verfügbar`;
		} catch {
			this.seaDownloadStatus = 'error';
		}
	}

	private findParentSection(node: Node): HTMLElement | null {
		let current: Node | null = node;
		while (current) {
			if (current instanceof HTMLElement && current.tagName === 'SECTION' && current.id) {
				return current;
			}
			current = current.parentNode;
		}
		return null;
	}

	private clearHighlights() {
		for (const el of this.highlightedElements) {
			el.classList.remove('search-highlight');
		}
		this.highlightedElements = [];
	}

	private onScroll(container: Element) {
		const containerRect = container.getBoundingClientRect();
		const offset = 100; // Offset vom oberen Rand

		// Scroll-Top-Button nur anzeigen wenn nicht ganz oben
		this.showScrollTopButton = container.scrollTop > 100;

		for (let i = this.sections.length - 1; i >= 0; i--) {
			const section = document.getElementById(this.sections[i]);
			if (section) {
				const sectionRect = section.getBoundingClientRect();
				const relativeTop = sectionRect.top - containerRect.top;

				if (relativeTop <= offset) {
					if (this.activeSection !== this.sections[i]) {
						this.activeSection = this.sections[i];
						this.scrollTocToActiveSection();
					}
					return;
				}
			}
		}
		if (this.activeSection !== this.sections[0]) {
			this.activeSection = this.sections[0];
			this.scrollTocToActiveSection();
		}
	}

	private scrollTocToActiveSection() {
		// Warte auf Angular Change Detection
		setTimeout(() => {
			const toc = document.querySelector('.toc');
			if (!toc) return;

			const activeLink = toc.querySelector('a.active');
			if (!activeLink) return;

			const tocRect = toc.getBoundingClientRect();
			const linkRect = activeLink.getBoundingClientRect();

			// Prüfen ob das Element außerhalb des sichtbaren Bereichs ist
			const isAbove = linkRect.top < tocRect.top + 80; // 80px Puffer oben (für Suchfeld + Überschrift)
			const isBelow = linkRect.bottom > tocRect.bottom - 20; // 20px Puffer unten

			if (isAbove || isBelow) {
				// Zum aktiven Element scrollen
				const scrollTop = toc.scrollTop + (linkRect.top - tocRect.top) - (tocRect.height / 2);
				toc.scrollTo({ top: scrollTop, behavior: 'smooth' });
			}
		}, 0);
	}
}
